import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { roadmapData } from '@/mocks/homepageData';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const quarterMap: Record<string, string> = {
  'Q1 2026': 'q1',
  'Q2 2026': 'q2',
  'Q3 2026': 'q3',
  'Q4 2026': 'q4',
};

export default function RoadmapSection() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.15 });

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textSecondary = isDark ? 'text-[#999]' : 'text-[#666]';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-[#00ff88] text-[#00ff88]';
      case 'in-progress':
        return 'bg-[#ffaa00] text-[#ffaa00]';
      default:
        return isDark ? 'bg-white/20 text-[#666]' : 'bg-black/10 text-[#888]';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return t('roadmap.completed');
      case 'in-progress':
        return t('roadmap.inProgress');
      default:
        return t('roadmap.upcoming');
    }
  };

  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const timelineBg = isDark
    ? 'from-[#00ff88]/30 via-white/10 to-[#00ff88]/30'
    : 'from-[#00ff88]/30 via-black/10 to-[#00ff88]/30';

  return (
    <section
      id="roadmap"
      ref={sectionRef}
      className={`relative w-full py-16 md:py-24 border-t ${borderLight}`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 md:mb-14"
        >
          <span className="text-xs text-[#00ff88] font-mono uppercase tracking-widest">{t('roadmap.ourJourney')}</span>
          <h2 className={`mt-3 text-2xl md:text-3xl lg:text-4xl font-bold ${textPrimary}`}>
            {t('roadmap.roadmap2026')}
          </h2>
          <p className={`mt-3 text-sm md:text-base max-w-2xl mx-auto ${textSecondary}`}>
            {t('roadmap.description')}
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative max-w-5xl mx-auto">
          {/* Desktop horizontal line */}
          <div className={`hidden lg:block absolute top-[72px] left-0 right-0 h-px bg-gradient-to-r ${timelineBg}`} />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-4">
            {roadmapData.map((item, index) => (
              <motion.div
                key={item.quarter}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="relative"
              >
                {/* Desktop dot on timeline */}
                <div className="hidden lg:flex justify-center mb-6">
                  <div
                    className={`w-3 h-3 rounded-full border-2 ${
                      item.status === 'completed'
                        ? 'bg-[#00ff88] border-[#00ff88]'
                        : item.status === 'in-progress'
                        ? 'bg-[#ffaa00] border-[#ffaa00]'
                        : isDark
                        ? 'bg-[#050505] border-white/30'
                        : 'bg-[#f5f5f5] border-black/20'
                    }`}
                  />
                </div>

                <div className="glass-card p-5 md:p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-sm font-mono font-semibold ${textPrimary}`}>
                      {item.quarter}
                    </span>
                    <span
                      className={`text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-opacity-10 border border-current border-opacity-20 ${getStatusColor(
                        item.status
                      )}`}
                    >
                      {getStatusLabel(item.status)}
                    </span>
                  </div>

                  <h3 className={`text-base font-semibold mb-3 ${textPrimary}`}>
                    {t(`content.roadmap.${quarterMap[item.quarter]}.title`, item.title)}
                  </h3>

                  <ul className="space-y-2">
                    {item.items.map((listItem, i) => (
                      <li key={i} className={`flex items-start gap-2 text-sm ${textSecondary}`}>
                        <i className="ri-check-line w-4 h-4 flex items-center justify-center text-[#00ff88] flex-shrink-0 mt-0.5" />
                        <span>{t(`content.roadmap.${quarterMap[item.quarter]}.items.${i}`, listItem)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}